import { Add, OpenInNew, Remove } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Typography,
} from '@mui/material';
import { AICompatibilityTable } from 'components/AITools/components/AICompatibilityTable';
import { SlideAIModalConfirmed } from 'components/AITools/components/SlideAIModalConfirmed';
import { SlideAIModalPaymentInfo } from 'components/AITools/components/SlideAIModalPaymentInfo';
import { useAIModelModalStyles } from 'components/AITools/constants/styles';
import {
  getIssuesCount,
  getShortenedSlidesPageUrl,
  isCompatibleDataMissing,
  isCompatibleSlide,
  isModelExists,
  isStainTypeMismatch,
} from 'components/AITools/utilities/common';
import { InformationMenu } from 'components/pages/AppLibrary/components/InformationMenu';
import { SlidesGallery } from 'components/pages/AppLibrary/SlidesGallery';
import { RUN_MODEL_ERROR_MESSAGE } from 'constants/errorMessages';
import logo from 'images/logos/lowResolutionLogo.png';
import { useEffect, useMemo, useState } from 'react';
import { APP_LIBRARY } from 'constants/urls';
import { useSelector } from 'react-redux';
import { useGetTeamsQuery } from 'store/apis/teamsApi';
import { useConfirmAIModelMutation } from 'store/apis/walletApi';
import { isStaffSelector } from 'store/slices/userDetailsSlice';
import { useSnackbar } from 'utilities/hooks/useSnackbar/useSnackbar';

export const SlideAIModalContent = ({
  currentModel,
  isProcessed,
  onClose,
  selectedSlides,
  handleReportOpen,
  model,
  onRunStart,
}) => {
  const { classes } = useAIModelModalStyles();
  const isStaff = useSelector(isStaffSelector);
  const [confirmPayment, { isLoading: isConfirming }] =
    useConfirmAIModelMutation();
  const { showError } = useSnackbar();
  const [team, setTeam] = useState();
  const { data: teams, isLoading: isTeamsLoading } = useGetTeamsQuery();
  const [isPaymentConfirmed, setIsPaymentConfirmed] = useState(isProcessed);
  const [availableToProcessSlides, setAvailableToProcessSlides] = useState(
    selectedSlides
      ?.filter(
        (slide) =>
          isCompatibleSlide(slide, model) &&
          !isModelExists(slide, model) &&
          !isCompatibleDataMissing(slide, model),
      )
      .map((slide) => slide.uuid),
  );

  const compatibleSlidesCount = useMemo(
    () =>
      selectedSlides?.filter((slide) => !isStainTypeMismatch(slide, model))
        .length,
    [selectedSlides, model],
  );

  const incompatibleSlidesCount = selectedSlides.length - compatibleSlidesCount;

  const issuesCount = useMemo(
    () => getIssuesCount(selectedSlides, model),
    [selectedSlides, model],
  );

  const handleConfirmPayment = (isForce = false) => {
    if (!team || (isForce && !isStaff)) return;

    const slidesWithoutResults = selectedSlides.filter(
      (slide) => !isModelExists(slide, model),
    );

    confirmPayment({
      currentPageUrl: getShortenedSlidesPageUrl(),
      teamUUID: team.uuid,
      walletUUID: team.wallet.uuid,
      modelId: currentModel.toolId,
      slidesUUIDs: isForce
        ? slidesWithoutResults.map((slide) => slide.uuid)
        : availableToProcessSlides,
    })
      .unwrap()
      .then(() => {
        onRunStart();
        setIsPaymentConfirmed(true);
      })
      .catch((e) => {
        const error = e?.data?.[0] || e?.data || RUN_MODEL_ERROR_MESSAGE;
        showError(error);
      });
  };

  const handleViewMore = () => {
    window.open(`${APP_LIBRARY}/${currentModel.toolId}`, 'blank');
  };

  const handleClose = () => {
    setIsPaymentConfirmed(false);
    onClose();
  };

  const isLoading = isConfirming || isTeamsLoading;

  useEffect(() => {
    if (teams?.length) {
      const { uuid, team_wallet: wallet } = teams[0];

      setTeam({ uuid, wallet });
    }
  }, [teams, setTeam]);

  const isAllSlidesAvailable =
    availableToProcessSlides?.length === selectedSlides.length;

  return (
    <Box className={classes.container}>
      {isPaymentConfirmed ? (
        <SlideAIModalConfirmed
          handleClose={handleClose}
          isProcessed={isProcessed}
          handleReportOpen={handleReportOpen}
        />
      ) : (
        <Box>
          <AICompatibilityTable
            issuesCount={issuesCount}
            incompatibleSlidesCount={incompatibleSlidesCount}
            selectedSlides={selectedSlides}
            model={model}
            availableToProcessSlides={availableToProcessSlides}
            setAvailableToProcessSlides={setAvailableToProcessSlides}
          />
          <SlideAIModalPaymentInfo
            model={currentModel}
            isAllSlidesAvailable={isAllSlidesAvailable}
            slidesCount={availableToProcessSlides?.length}
            handleConfirmPayment={() => handleConfirmPayment(false)}
            handleUnsafeConfirmPayment={() => handleConfirmPayment(true)}
            isConfirming={isLoading}
            balance={+(team?.wallet?.balance || 0)}
          />
        </Box>
      )}
      <Accordion
        className={!isPaymentConfirmed ? classes.infoSection : undefined}
        disableGutters
        defaultExpanded
      >
        <AccordionSummary className={classes.accordionSummary}>
          <Typography className={classes.title}>
            Learn More About {currentModel.title}
          </Typography>
          <Box className={classes.expandIcon}>
            <Add className="expand" />
            <Remove className="collapse" />
          </Box>
        </AccordionSummary>
        <AccordionDetails className={classes.accordionDetails}>
          <Box className={classes.halfContainer}>
            <Box className={classes.galleryWrapper}>
              <SlidesGallery
                showThumbs={false}
                maxWidth="100%"
                showArrows
                content={[
                  currentModel.video,
                  ...currentModel.toolImages[0].toolImage,
                ]}
              />
            </Box>
            <Box>
              <InformationMenu
                isSmall
                model={{
                  ...currentModel,
                  developer: (
                    <img src={logo} alt="HistoWiz" className={classes.logo} />
                  ),
                }}
              />
              <Button
                variant="contained"
                color="default"
                className={classes.confirmButton}
                endIcon={<OpenInNew fontSize="small" />}
                onClick={handleViewMore}
              >
                View Details
              </Button>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
